import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

// ============================================================================
// Candidate intake page — public, mobile-first (Phase B, 2026-08-21 pt.16).
// URL: /hiring/intake/{token_id}. NO auth. Multi-step: Contact → Hard-gate
// → Open-ended → Review & submit. Progress bar + resumable via localStorage.
// Backend: GET /api/hiring/intake/{id}, POST .../submit
// ============================================================================

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STEPS = [
  { key: "contact",      label: "About you" },
  { key: "hard_gate",    label: "Quick check" },
  { key: "open_ended",   label: "Tell us more" },
  { key: "review",       label: "Review" },
];

const LS_KEY = (token) => `cc_hiring_intake_${token}`;

export default function HiringIntake() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, meta: null, submitted: false });
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState({
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    hard_gate: {},        // key → answer
    open_ended: {},       // key → answer
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await axios.get(`${BACKEND}/api/hiring/intake/${token}`);
        setState({ loading: false, error: null, meta: data, submitted: data.submitted });
        // Prefill from server + resume from localStorage.
        const stored = JSON.parse(localStorage.getItem(LS_KEY(token)) || "null");
        setForm((f) => ({
          ...f,
          contact_name: stored?.contact_name || data.prefill?.name || "",
          contact_email: stored?.contact_email || data.prefill?.email || "",
          contact_phone: stored?.contact_phone || data.prefill?.phone || "",
          hard_gate: stored?.hard_gate || {},
          open_ended: stored?.open_ended || {},
        }));
        if (stored?.stepIdx) setStepIdx(stored.stepIdx);
      } catch (e) {
        setState({ loading: false, error: e.response?.data?.detail || "Failed to load — the link may be invalid or expired.", meta: null, submitted: false });
      }
    }
    load();
  }, [token]);

  // Persist form on every change so mobile users can close & resume.
  useEffect(() => {
    if (state.loading || state.submitted) return;
    localStorage.setItem(LS_KEY(token), JSON.stringify({ ...form, stepIdx }));
  }, [form, stepIdx, state.loading, state.submitted, token]);

  async function submit() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // 2026-08-24 pt.20 — Filter out hidden hard-gate questions
      // (e.g. license_state / reciprocity_timeline when Unlicensed
      // is chosen). Previously they went to the backend as `answer: 0`
      // which downstream reviewers could misread as a real numeric
      // response. Hidden means "not applicable" — omit entirely.
      const hgQs = state.meta.hard_gate_questions || [];
      const hgVisible = computeVisibility(hgQs, form.hard_gate);
      const payload = {
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone || null,
        hard_gate_answers: hgQs.filter(hgVisible).map((q) => ({
          key: q.key,
          answer: form.hard_gate[q.key] ?? (q.type === "boolean" ? false : 0),
        })),
        open_ended_answers: (state.meta.open_ended_questions || []).map((q) => ({
          key: q.key,
          answer: form.open_ended[q.key] || "",
        })),
      };
      const { data } = await axios.post(
        `${BACKEND}/api/hiring/intake/${token}/submit`, payload,
      );
      setOutcome(data);
      localStorage.removeItem(LS_KEY(token));
    } catch (e) {
      setSubmitError(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (state.loading) return <PageShell><CenteredSpinner /></PageShell>;
  if (state.error) return <PageShell><ErrorBox message={state.error} /></PageShell>;
  if (state.submitted && !outcome) {
    // Reload after a fresh submit → show a friendly notice
    return <PageShell><ThankYou already /></PageShell>;
  }
  if (outcome) {
    return (
      <PageShell>
        {outcome.outcome === "advanced_to_stage_1"
          ? <ThankYou />
          : <WarmDecline reason={outcome.reason} />}
      </PageShell>
    );
  }

  const step = STEPS[stepIdx];
  const canNext = validateStep(step.key, form, state.meta);

  return (
    <PageShell>
      <Header meta={state.meta} />
      <ProgressBar stepIdx={stepIdx} steps={STEPS} />

      <div className="bg-white rounded-lg shadow-sm border border-[#e5e7eb] p-5 mt-4" data-testid={`hiring-intake-step-${step.key}`}>
        {step.key === "contact" && <ContactStep form={form} setForm={setForm} />}
        {step.key === "hard_gate" && <HardGateStep form={form} setForm={setForm} questions={state.meta.hard_gate_questions || []} />}
        {step.key === "open_ended" && <OpenEndedStep form={form} setForm={setForm} questions={state.meta.open_ended_questions || []} />}
        {step.key === "review" && <ReviewStep form={form} meta={state.meta} />}
      </div>

      {submitError && (
        <div className="mt-3 text-sm text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-3" data-testid="hiring-intake-error">
          <AlertTriangle className="inline w-4 h-4 mr-1.5 align-text-bottom" />
          {submitError}
        </div>
      )}

      <div className="flex justify-between items-center mt-6">
        <Button
          variant="outline"
          disabled={stepIdx === 0}
          onClick={() => setStepIdx((s) => Math.max(0, s - 1))}
          data-testid="hiring-intake-back"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        {stepIdx < STEPS.length - 1 ? (
          <Button
            onClick={() => setStepIdx((s) => s + 1)}
            disabled={!canNext}
            data-testid="hiring-intake-next"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={submit}
            disabled={submitting || !canNext}
            data-testid="hiring-intake-submit"
            className="bg-[#0072bc] hover:bg-[#005a9c] text-white"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Submit application
          </Button>
        )}
      </div>
    </PageShell>
  );
}

function validateStep(key, form, meta) {
  if (key === "contact") {
    return !!(form.contact_name.trim() && /\S+@\S+\.\S+/.test(form.contact_email));
  }
  if (key === "hard_gate") {
    const isVisible = computeVisibility(meta.hard_gate_questions || [], form.hard_gate);
    const visible = (meta.hard_gate_questions || []).filter(isVisible);
    return visible.every((q) => {
      const v = form.hard_gate[q.key];
      if (q.type === "string") return typeof v === "string" && v.trim().length > 0;
      return v !== undefined && v !== "";
    });
  }
  if (key === "open_ended") {
    // 2026-08-24 pt.19 — every question flagged `required` must have
    // a non-blank answer before the Next/Submit button unlocks.
    const qs = meta.open_ended_questions || [];
    return qs.every((q) => {
      if (!q.required) return true;
      const v = form.open_ended[q.key];
      return typeof v === "string" && v.trim().length > 0;
    });
  }
  return true;
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-[#f9fafb] py-6 px-4">
      <div className="max-w-2xl mx-auto">
        {children}
      </div>
    </div>
  );
}

function Header({ meta }) {
  return (
    <div className="mb-4" data-testid="hiring-intake-header">
      <div className="text-xs text-[#4b5563] tracking-wide uppercase">Application</div>
      <h1 className="text-xl font-semibold text-[#0e1420]">
        {meta.position_title} — {meta.company_name}
      </h1>
    </div>
  );
}

function ProgressBar({ stepIdx, steps }) {
  const pct = ((stepIdx) / (steps.length - 1)) * 100;
  return (
    <div className="mb-2" data-testid="hiring-intake-progress">
      <div className="h-1.5 w-full bg-[#e5e7eb] rounded overflow-hidden">
        <div className="h-full bg-[#0072bc] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between text-[11px] text-[#4b5563] mt-1">
        {steps.map((s, i) => (
          <span
            key={s.key}
            className={i <= stepIdx ? "text-[#0072bc] font-medium" : ""}
            data-testid={`hiring-intake-progress-${s.key}`}
          >
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ContactStep({ form, setForm }) {
  return (
    <div className="space-y-3">
      <Field label="Full name">
        <Input
          data-testid="hiring-intake-contact-name"
          value={form.contact_name}
          onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
          placeholder="Jane Doe"
        />
      </Field>
      <Field label="Email address">
        <Input
          data-testid="hiring-intake-contact-email"
          type="email"
          value={form.contact_email}
          onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
          placeholder="jane@example.com"
        />
      </Field>
      <Field label="Phone number (optional)">
        <Input
          data-testid="hiring-intake-contact-phone"
          type="tel"
          value={form.contact_phone}
          onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))}
          placeholder="(417) 555-1234"
        />
      </Field>
    </div>
  );
}

// Shared recursive visibility check — used by HardGateStep (which
// filters the render list) and validateStep (which decides if the
// step is complete). A child of a hidden parent is ALSO hidden, so a
// stale answer on a now-hidden parent can't force a demand on its
// children. Fixes the pt.17 Unlicensed→KS→reciprocity leak Brett
// caught in Preview.
function computeVisibility(questions, answers) {
  const qByKey = Object.fromEntries(questions.map((q) => [q.key, q]));
  const cache = {};
  const isBlank = (v) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");
  function visible(q) {
    if (q.key in cache) return cache[q.key];
    const cond = q.show_if;
    if (!cond) return (cache[q.key] = true);
    for (const [op, arg] of Object.entries(cond)) {
      let parentKey, kind;
      if (op.endsWith("_not_in")) { parentKey = op.slice(0, -"_not_in".length); kind = "not_in"; }
      else if (op.endsWith("_not_equals")) { parentKey = op.slice(0, -"_not_equals".length); kind = "not_equals"; }
      else continue;
      const parentQ = qByKey[parentKey];
      if (parentQ && !visible(parentQ)) return (cache[q.key] = false);
      const parentAns = answers[parentKey];
      if (isBlank(parentAns)) return (cache[q.key] = false);
      if (kind === "not_in" && arg.includes(parentAns)) return (cache[q.key] = false);
      if (kind === "not_equals" && parentAns === arg) return (cache[q.key] = false);
    }
    return (cache[q.key] = true);
  }
  return visible;
}

function HardGateStep({ form, setForm, questions }) {
  const isVisible = computeVisibility(questions, form.hard_gate);
  return (
    <div className="space-y-4">
      {questions.filter(isVisible).map((q) => (
        <div key={q.key} data-testid={`hiring-intake-hg-${q.key}`}>
          <div className="text-sm font-medium text-[#0e1420] mb-2">{q.label}</div>
          {q.type === "boolean" ? (
            <div className="flex gap-2">
              {[["Yes", true], ["No", false]].map(([label, val]) => (
                <button
                  key={label}
                  type="button"
                  data-testid={`hiring-intake-hg-${q.key}-${label.toLowerCase()}`}
                  onClick={() => setForm((f) => ({ ...f, hard_gate: { ...f.hard_gate, [q.key]: val } }))}
                  className={`px-4 py-2 rounded border text-sm font-medium min-w-[80px] transition-colors ${
                    form.hard_gate[q.key] === val
                      ? "bg-[#0072bc] text-white border-[#0072bc]"
                      : "bg-white text-[#0e1420] border-[#d1d5db] hover:bg-[#f3f4f6]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : q.type === "enum" ? (
            <div className="flex flex-wrap gap-2">
              {(q.options || []).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  data-testid={`hiring-intake-hg-${q.key}-${opt.toLowerCase()}`}
                  onClick={() => setForm((f) => ({ ...f, hard_gate: { ...f.hard_gate, [q.key]: opt } }))}
                  className={`px-3 py-2 rounded border text-sm font-medium transition-colors ${
                    form.hard_gate[q.key] === opt
                      ? "bg-[#0072bc] text-white border-[#0072bc]"
                      : "bg-white text-[#0e1420] border-[#d1d5db] hover:bg-[#f3f4f6]"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          ) : q.type === "integer" ? (
            <Input
              type="number"
              min="0"
              data-testid={`hiring-intake-hg-${q.key}-input`}
              value={form.hard_gate[q.key] ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, hard_gate: { ...f.hard_gate, [q.key]: e.target.value === "" ? "" : Number(e.target.value) } }))}
            />
          ) : q.type === "string" ? (
            <Input
              type="text"
              data-testid={`hiring-intake-hg-${q.key}-input`}
              value={form.hard_gate[q.key] ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, hard_gate: { ...f.hard_gate, [q.key]: e.target.value } }))}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function OpenEndedStep({ form, setForm, questions }) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-[#4b5563]">
        Please answer all questions. Your responses help us understand how you think about the work.
      </p>
      {questions.map((q) => (
        <div key={q.key} data-testid={`hiring-intake-oe-${q.key}`}>
          <label className="text-sm font-medium text-[#0e1420] block mb-1.5">
            {q.label}
            {q.required && <span className="text-[#dc2626] ml-1">*</span>}
          </label>
          <Textarea
            data-testid={`hiring-intake-oe-${q.key}-input`}
            rows={4}
            value={form.open_ended[q.key] || ""}
            onChange={(e) => setForm((f) => ({ ...f, open_ended: { ...f.open_ended, [q.key]: e.target.value } }))}
          />
        </div>
      ))}
    </div>
  );
}

function ReviewStep({ form, meta }) {
  // 2026-08-24 pt.19 — Reuse the same visibility rules the hard-gate
  // step uses. A candidate who picks "Unlicensed" must NOT see
  // license_state / reciprocity_timeline on Review (previously
  // rendered `undefined` for the hidden ones).
  const hgQuestions = meta.hard_gate_questions || [];
  const isVisible = computeVisibility(hgQuestions, form.hard_gate);
  const visibleHg = hgQuestions.filter(isVisible);
  const oeQuestions = meta.open_ended_questions || [];
  return (
    <div className="space-y-4">
      <p className="text-xs text-[#4b5563]">
        Please review your answers before submitting. You can go back to edit.
      </p>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-[#4b5563] mb-1">Contact</div>
        <div className="text-sm" data-testid="hiring-intake-review-contact">
          {form.contact_name} · {form.contact_email}{form.contact_phone ? ` · ${form.contact_phone}` : ""}
        </div>
      </div>
      <div data-testid="hiring-intake-review-hard-gate">
        <div className="text-xs font-semibold uppercase tracking-wide text-[#4b5563] mb-1">Quick check</div>
        <ul className="space-y-1">
          {visibleHg.map((q) => {
            const a = form.hard_gate[q.key];
            let rendered;
            if (typeof a === "boolean") rendered = a ? "Yes" : "No";
            else if (a === undefined || a === null || a === "") rendered = "—";
            else rendered = String(a);
            return (
              <li key={q.key} className="text-sm" data-testid={`hiring-intake-review-hg-${q.key}`}>
                <span className="text-[#4b5563]">{q.label}:</span>{" "}
                <span className="text-[#0e1420] font-medium">{rendered}</span>
              </li>
            );
          })}
        </ul>
      </div>
      {oeQuestions.length > 0 && (
        <div data-testid="hiring-intake-review-open-ended">
          <div className="text-xs font-semibold uppercase tracking-wide text-[#4b5563] mb-1">Tell us more</div>
          <ul className="space-y-3">
            {oeQuestions.map((q) => {
              const a = form.open_ended[q.key];
              const answer = typeof a === "string" && a.trim() ? a : "—";
              return (
                <li
                  key={q.key}
                  className="text-sm"
                  data-testid={`hiring-intake-review-oe-${q.key}`}
                >
                  <div className="text-[#4b5563]">{q.label}</div>
                  <div className="text-[#0e1420] whitespace-pre-wrap mt-0.5">
                    {answer}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-[#4b5563] block mb-1">{label}</label>
      {children}
    </div>
  );
}

function CenteredSpinner() {
  return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#4b5563]" /></div>;
}

function ErrorBox({ message }) {
  return (
    <div className="bg-white rounded-lg border border-[#f87171] p-6 mt-8" data-testid="hiring-intake-load-error">
      <AlertTriangle className="w-6 h-6 text-[#dc2626] mb-2" />
      <div className="text-[#0e1420] font-semibold mb-1">Unable to load your application</div>
      <div className="text-sm text-[#4b5563]">{message}</div>
    </div>
  );
}

function ThankYou({ already = false }) {
  return (
    <div className="bg-white rounded-lg border border-[#e5e7eb] p-8 mt-8 text-center" data-testid="hiring-intake-thankyou">
      <CheckCircle2 className="w-12 h-12 text-[#16a34a] mx-auto mb-3" />
      <div className="text-lg font-semibold text-[#0e1420] mb-2">
        {already ? "You've already submitted this application" : "Thanks — you're in!"}
      </div>
      <div className="text-sm text-[#4b5563]">
        We&apos;ll review and get back to you within 1 business day.
      </div>
    </div>
  );
}

function WarmDecline() {
  return (
    <div className="bg-white rounded-lg border border-[#e5e7eb] p-8 mt-8" data-testid="hiring-intake-decline">
      <div className="text-lg font-semibold text-[#0e1420] mb-2">Thanks for applying</div>
      <div className="text-sm text-[#4b5563]">
        We appreciate your interest but won&apos;t be able to move forward at
        this time. We wish you the best in your search.
      </div>
    </div>
  );
}
